'use client';

import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { SubscriptionPlanCards } from '@/components/features/pricing/SubscriptionPlanCards';
import type { SubscriptionPlanSummary } from '@/types/subscription';
import { useToast } from '@/hooks/use-toast';
import useAuthGuard from '@/hooks/useAuthGuard';
import { useEventTracking } from '@/hooks/useEventTracking';
import { EventType } from '@/api/types/events';

const FREE_ACCESS_ITEMS = [
  { label: 'Job search across 8M+ listings', href: '/jobs' },
  { label: 'Company profiles', href: '/companies' },
  { label: 'People and contact search', href: '/d/contacts/search' },
  { label: 'Resume builder', href: '/resume-builder' },
  { label: 'Outreach message generator', href: '/d/engage/compose' },
] as const;

type PricingClientProps = {
  plans: SubscriptionPlanSummary[];
};

export function PricingClient({ plans }: PricingClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const { handleAction: handleAuthAction } = useAuthGuard();
  const { trackEvent } = useEventTracking();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  useEffect(() => {
    trackEvent({
      event_type: EventType.NAVIGATION,
      element_id: 'pricing-page',
      event_details: {
        action: 'pricing_open',
        funnel: 'subscription_upgrade',
        source: 'pricing_page',
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackEvent]);

  const startCheckoutForPlan = async (planSlug: string, promoCode?: string, retriedAfterAuth = false) => {
    try {
      setLoadingPlan(planSlug);
      trackEvent({
        event_type: EventType.CLICK,
        element_id: 'pricing-checkout-start',
        event_details: {
          action: 'checkout_start',
          funnel: 'subscription_upgrade',
          source: 'pricing_page',
          plan_slug: planSlug,
          ...(promoCode ? { promo_code: promoCode } : {}),
        },
      });

      const checkoutBody: Record<string, string> = { planSlug };
      if (promoCode) {
        checkoutBody.promoCode = promoCode;
      }
      // Stripe's back button should return to the current page
      if (typeof window !== 'undefined') {
        checkoutBody.cancelRedirect = window.location.href;
      }

      const response = await fetch('/api/subscription/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checkoutBody),
      });

      if (response.status === 401) {
        trackEvent({
          event_type: EventType.CLICK,
          element_id: 'pricing-checkout-auth-required',
          event_details: {
            action: 'checkout_auth_required',
            funnel: 'subscription_upgrade',
            source: 'pricing_page',
            plan_slug: planSlug,
          },
        });
        if (!retriedAfterAuth) {
          setLoadingPlan(null);
          handleAuthAction(() => {
            void startCheckoutForPlan(planSlug, promoCode, true);
          }, 'sign-in');
          return;
        }
        throw new Error('Your session expired. Please sign in and try again.');
      }

      const data = await response.json().catch(() => ({}));

      if (response.status === 409 && data?.code === 'ALREADY_SUBSCRIBED') {
        trackEvent({
          event_type: EventType.CLICK,
          element_id: 'pricing-checkout-already-subscribed',
          event_details: {
            action: 'checkout_already_subscribed',
            funnel: 'subscription_upgrade',
            source: 'pricing_page',
            plan_slug: planSlug,
          },
        });
        const managePath =
          typeof data.managePath === 'string'
            ? data.managePath
            : '/settings/subscription';
        router.push(managePath);
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      if (typeof data.url === 'string' && data.url.length > 0) {
        trackEvent({
          event_type: EventType.CONVERSION,
          element_id: 'pricing-checkout-created',
          event_details: {
            action: 'checkout_session_created',
            funnel: 'subscription_upgrade',
            source: 'pricing_page',
            plan_slug: planSlug,
            ...(promoCode ? { promo_code: promoCode } : {}),
          },
        });
        window.location.href = data.url;
        return;
      }

      throw new Error('Failed to create checkout session');
    } catch (error) {
      trackEvent({
        event_type: EventType.ERROR,
        element_id: 'pricing-checkout-error',
        event_details: {
          action: 'checkout_failed',
          funnel: 'subscription_upgrade',
          source: 'pricing_page',
          plan_slug: planSlug,
          message: error instanceof Error ? error.message : 'unknown_error',
        },
      });
      console.error('Error creating checkout session:', error);
      toast({
        title: 'Checkout failed',
        description: error instanceof Error ? error.message : 'Failed to start checkout process',
        variant: 'destructive',
      });
      setLoadingPlan(null);
    }
  };

  const handleSubscribe = (plan: SubscriptionPlanSummary, promoCode?: string) => {
    if (!plan.slug) {
      toast({
        title: 'Invalid plan',
        description: 'Please select a valid plan.',
        variant: 'destructive',
      });
      return;
    }

    handleAuthAction(() => {
      void startCheckoutForPlan(plan.slug, promoCode);
    }, 'sign-in');
  };

  return (
    <div className="mb-20">
      <SubscriptionPlanCards
        plans={plans}
        mode="pricing"
        onSubscribe={handleSubscribe}
        isProcessingPlanSlug={loadingPlan}
      />

      {/* Always Free block */}
      <div className="mx-auto mt-8 max-w-5xl">
        <div
          className="border border-border border-t border-t-primary bg-card p-6 lg:p-8"
          style={{ boxShadow: 'var(--shadow-warm-md)' }}
        >
          <h3 className="text-lg font-extrabold text-foreground uppercase tracking-tight mb-1">
            Always Free
          </h3>
          <p className="text-sm text-muted-foreground font-medium mb-4">
            Start free with powerful tools — upgrade only when you want auto-apply and auto-outreach running for you.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {FREE_ACCESS_ITEMS.map((item) => (
              <Link
                key={item.label}
                href={item.href}
                className="inline-flex items-center rounded-sm border border-border bg-background px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
